<script setup>
  import AppSidebar from "@/components/App/Sidebar/AppSidebar.vue";
  import { computed, ref } from "vue";
  import {
    MessageCircle,
    Mail,
    Target,
    Tag,
    Database,
    Play,
    Building2,
    Users,
    UserCog,
    File,
    FileText,
    Languages,
    LayoutTemplate,
    Palette,
    Proportions,
    TableProperties,
    LayoutGrid,
    Webhook,
    Megaphone,
    Gauge,
    Bell,
    Images,
    ListChecks,
  } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { userType } from "@utils/constants/userRoles";
  import { useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useCurrentUserStore } from "@stores/currentUser.js";
  const { user, canWriteSurveys, canMonitorSurveys, canAnalyzeSurveys, canManageReportThemes, canManageChatPages } =
    storeToRefs(useCurrentUserStore());

  const route = useRoute();

  const hasEmailingAccess = computed(
    () =>
      [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type) ||
      (user.value?.has_mailing_access && canWriteSurveys.value),
  );

  const navigation = computed(() => [
    {
      title: "Workspace",
      isOpen: ref(true),
      items: [
        {
          title: "Surveys",
          url: "/",
          icon: MessageCircle,
          isActive: route.path === "/" || route.path.startsWith("/surveys"),
          if: canWriteSurveys.value || canMonitorSurveys.value || canAnalyzeSurveys.value,
        },
        {
          title: "Episto AI",
          url: "/episto-ai",
          icon: AiStar,
          isActive: route.path.startsWith("/episto-ai"),
          badge: "beta",
          badgeVariant: "success",
          if: canWriteSurveys.value || canAnalyzeSurveys.value,
        },
        {
          title: "Email Templates",
          url: "/mailing/templates",
          isActive: route.path.startsWith("/mailing/templates"),
          icon: Mail,
          if: hasEmailingAccess.value,
        },
        {
          title: "Report Themes",
          url: "/report-themes",
          isActive: route.path.startsWith("/report-themes"),
          icon: Palette,
          badge: "beta",
          badgeVariant: "teal",
          if: canManageReportThemes.value,
        },
        {
          title: "Chat pages",
          url: "/chat-pages",
          isActive: route.path.startsWith("/chat-pages"),
          icon: File,
          if: canManageChatPages.value,
        },
      ],
    },
    {
      title: "Admin",
      isOpen: ref(true),
      items: [
        {
          title: "Campaigns",
          url: "/admin/m_campaigns",
          icon: Target,
          isActive: route.path.startsWith("/admin/m_campaigns"),
          if: [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type),
        },
        {
          title: "Media library",
          url: "/admin/media-library",
          icon: Images,
          isActive: route.path.startsWith("/admin/media-library"),
          if: [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type),
        },
        {
          title: "Campaigns pivot",
          url: "/admin/campaigns-pivot",
          icon: LayoutGrid,
          isActive: route.path.startsWith("/admin/campaigns-pivot"),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Pricer",
          url: "/admin/pricer/projects",
          icon: Tag,
          isActive: route.path.startsWith("/admin/pricer"),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Histo",
          url: "/histo",
          icon: Database,
          isActive: route.path.startsWith("/histo"),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Jobs",
          url: "/admin/jobs",
          icon: Play,
          isActive: route.path.startsWith("/admin/jobs"),
          if: [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type),
        },
        {
          title: "SQL",
          url: "/admin/sql",
          icon: TableProperties,
          isActive: route.path.startsWith("/admin/sql"),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Accounts",
          url: "/admin/accounts",
          icon: Building2,
          isActive: route.path.startsWith("/admin/accounts"),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Users",
          url: "/admin/users",
          icon: Users,
          isActive: route.path.startsWith("/admin/users"),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Roles",
          url: "/admin/roles",
          icon: UserCog,
          isActive: route.path.startsWith("/admin/roles"),
          if: user.value?.user_type === userType.SUPER_ADMIN,
        },
        {
          title: "Hooks",
          url: "/admin/webhooks",
          icon: Webhook,
          isActive: route.path.startsWith("/admin/webhooks"),
          if: [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type),
        },
        {
          title: "Announcements",
          url: "/admin/announcements",
          icon: Bell,
          isActive: route.path.startsWith("/admin/announcements"),
          if: user.value?.user_type === userType.SUPER_ADMIN,
        },
        {
          title: "Ad templates",
          url: "/admin/ad-templates",
          icon: LayoutTemplate,
          isActive: route.path.startsWith("/admin/ad-templates"),
          if: [userType.ADMIN, userType.SUPER_ADMIN].includes(user.value?.user_type),
        },
        {
          title: "Default Messages",
          url: "/admin/default-messages",
          icon: Languages,
          isActive: route.path.startsWith("/admin/default-messages"),
          if: user.value?.user_type === userType.SUPER_ADMIN,
        },
        {
          title: "Scripting guidelines",
          url: "/admin/scripting-guidelines",
          icon: ListChecks,
          isActive: route.path.startsWith("/admin/scripting-guidelines"),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Contracts",
          url: "/contracts",
          icon: FileText,
          isActive: route.path.startsWith("/contracts"),
          if: [userType.ADMIN, userType.SUPER_ADMIN, userType.SALES].includes(user.value?.user_type),
        },
        {
          title: "Master Reports",
          url: "/master-reports",
          isActive: route.path.startsWith("/master-reports"),
          icon: Proportions,
          if: user.value?.user_type === userType.SUPER_ADMIN,
        },
      ],
    },
    {
      title: "JCDecaux",
      isOpen: ref(true),
      items: [
        {
          title: "Campagnes",
          url: "/jcd-campaigns",
          icon: Megaphone,
          isActive: route.path.startsWith("/jcd-campaigns"),
          if: user.value?.["jcd_access?"] ?? false,
        },
        {
          title: "Benchmarks",
          url: "/jcd-benchmarks",
          icon: Gauge,
          isActive: route.path.startsWith("/jcd-benchmarks"),
          if: user.value?.["jcd_access?"] ?? false,
        },
      ],
    },
  ]);
</script>

<template>
  <AppSidebar :navigation="navigation" />
</template>
